import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { ErrorService } from '../../services/error.service'
import { KeyingDataSets } from '../../services/keying.data.sets.service'
import { KeyingDataSetsService } from '../../services/keying.data.sets.service'
import { Loading, LoadingService } from '../../services/loading.service'
import { LoggingService } from '../../services/logging.service'

@Component({
    selector: 'app-new-key-system-dialog',
    templateUrl: './new-key-system-dialog.component.html',
    styleUrls: ['./new-key-system-dialog.component.scss'],
    standalone: false
})
export class NewKeySystemDialogComponent implements OnInit, OnDestroy {
  destroy$ = new Subject()
  newStructureForm: FormGroup

  loading: Loading

  combinatingRuleSets = []
  selectedCombinatingRuleSet

  constructionTypes = []
  selectedConstructionKeyType

  keywayFamilies = []
  selectedKeywayFamily

  chamberTypes = []
  selectedChamberType

  defaultTMKName = 'TMK'

  focus = {
    combinatingRuleSet: false,
    constructionKeyType: false,
    keywayFamily: false,
    chamberType: false,
    tmkName: false,
  }

  public errorMessages = {
    combinatingRuleSet: [{ type: 'required', message: 'A Combinating Rule Set is required.' }],
    constructionKeyType: [{ type: 'required', message: 'A Construction Key Type is required.' }],
    keywayFamily: [{ type: 'required', message: 'A Keyway Family is required.' }],
    tmkName: [
      { type: 'required', message: 'TMK Name is required.' },
      { type: 'nameExists', message: 'TMK Name is already in use.' },
    ],
    chamberType: [{ type: 'required', message: 'TMK Name is required.' }],
  }

  isDisabled: boolean = true

  constructor(
    private dialogRef: MatDialogRef<NewKeySystemDialogComponent>,
    public form: FormBuilder,
    public keyingDataSetsService: KeyingDataSetsService,
    private loadingService: LoadingService,
    private errorService: ErrorService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private loggingService: LoggingService
  ) {
    this.newStructureForm = this.form.group({
      combinatingRuleSet: new FormControl({ value: '', disabled: this.isDisabled }, [Validators.required]),
      constructionKeyType: new FormControl('', [Validators.required]),
      keywayFamily: new FormControl({ value: '', disabled: this.isDisabled }, [Validators.required]),
      chamberType: new FormControl({ value: '', disabled: this.isDisabled }, [Validators.required]),
      tmkName: new FormControl('', [Validators.required, this.duplicateTmkValidator]),
      hasDocuments: new FormControl(true, [Validators.required]),
    })
  }

  /* istanbul ignore next */
  ngOnInit(): void {
    this.loggingService.log('Data:', this.data)
    this.loadingService.loading$.pipe(takeUntil(this.destroy$)).subscribe((loading) => {
      this.loading = loading
    })

    this.setFormData()
  }

  /* istanbul ignore next */
  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  /* istanbul ignore next */
  setKeywayFamily(keywayFamilies: any[]) {
    this.keywayFamilies = keywayFamilies
    this.selectedKeywayFamily = this.keywayFamilies.find((kwf) => kwf.id === this.data.keywayFamilyId)
    this.newStructureForm.controls.keywayFamily.setValue(this.selectedKeywayFamily)
  }

  /* istanbul ignore next */
  setCombinatingRuleSet(combinatingRuleSets: any[]) {
    this.combinatingRuleSets = combinatingRuleSets
    this.selectedCombinatingRuleSet = this.combinatingRuleSets.find((crs) => crs.id === this.data.combinatingRuleSetId)
    this.newStructureForm.controls.combinatingRuleSet.setValue(this.selectedCombinatingRuleSet)
  }

  /* istanbul ignore next */
  setChamberType(chamberTypes: any[]) {
    this.chamberTypes = chamberTypes
    this.selectedChamberType = this.chamberTypes.find((ct) => ct.id === this.data.chamberTypeId)
    this.newStructureForm.controls.chamberType.setValue(this.selectedChamberType)
  }

  /* istanbul ignore next */
  filterConstructionTypes(constructionTypes: any[]) {
    this.constructionTypes = constructionTypes.filter((x) =>
      x.attributes.some(
        (attr) =>
          attr.keywayFamilyId === this.selectedKeywayFamily.id && attr.chamberTypeId === this.selectedChamberType.id
      )
    )
  }

  /* istanbul ignore next */
  setConstructionTypes(constructionTypes: any[]) {
    if (constructionTypes && constructionTypes.length > 0) {
      this.filterConstructionTypes(constructionTypes)
      this.selectedConstructionKeyType = this.constructionTypes[0]
      this.newStructureForm.controls.constructionKeyType.setValue(this.selectedConstructionKeyType)

      this.newStructureForm.controls.constructionKeyType.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          this.checkTmkNameStatus()
        })
    }
  }

  /* istanbul ignore next */
  setFormData() {
    this.keyingDataSetsService.keyingDataSets$.pipe(takeUntil(this.destroy$)).subscribe(
      (keyingDataSets: KeyingDataSets) => {
        if (keyingDataSets) {
          this.setKeywayFamily(keyingDataSets.keywayFamilies)
          this.setCombinatingRuleSet(keyingDataSets.combinatingRuleSets)
          this.setChamberType(keyingDataSets.chamberTypes)
          this.setConstructionTypes(keyingDataSets.constructionTypes)
        }
      },
      (error) => {
        this.errorService.throw(error)
        this.close()
      }
    )
  }

  // Forms
  /* istanbul ignore next */
  createStructure() {
    if (this.newStructureForm.valid) {
      const controls = this.newStructureForm.controls

      const data = {
        keySystem: {
          topMasterKeyName: controls.tmkName.value,
          constructionTypeId: controls.constructionKeyType.value.id,
          manufacturerId: 1,
        },
        masterKeys: {
          numberOfGrandMasters: 1,
          numberOfMasters: 1,
          numberOfSubMasters: 1,
          numberOfChanges: 1,
        },
      }

      this.dialogRef.close(data)
    }
  }

  /* istanbul ignore next */
  tmkHasDefault() {
    const retVal =
      this.newStructureForm.controls.keywayFamily.value &&
      this.selectedConstructionKeyType.id == 3 &&
      (!this.newStructureForm.controls.tmkName.value ||
        this.newStructureForm.controls.tmkName.value == this.defaultTMKName)
    return retVal
  }

  /* istanbul ignore next */
  checkTmkNameStatus() {
    if (this.tmkHasDefault()) {
      this.newStructureForm.controls.tmkName.setValue(this.defaultTMKName)
      this.newStructureForm.controls.tmkName.disable()
    } else {
      this.newStructureForm.controls.tmkName.enable()
    }
  }

  /* istanbul ignore next */
  focusOn(focus) {
    this.focus[focus] = !this.focus[focus]
  }

  /* istanbul ignore next */
  fieldHasErrorMessages(form, validation, fieldName) {
    const field = form.get(fieldName)

    return Boolean(field.hasError(validation.type) && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  fieldHasError(form, fieldName) {
    const field = form.get(fieldName)
    return Boolean(field.invalid && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  compareObjs(o1: any, o2: any) {
    return o1 && o2 && o1.id === o2.id
  }

  /* istanbul ignore next */
  duplicateTmkValidator = (control: FormControl) => {
    let value = control.value
    if (value !== '' && value !== null) {
      const nameExists = this.data.keySystems.some((ks) => ks.topMasterKeyName === value.toUpperCase())
      if (nameExists) {
        return { nameExists, type: 'nameExists' }
      }
    }
    return null
  }

  // Dialog
  /* istanbul ignore next */
  close() {
    this.newStructureForm.reset()
    this.dialogRef.close()
  }
}
